import { useEffect } from "react";
import axios from "axios";
import useRefreshToken from './../hooks/useRefreshToken';
import useAuth from "./useAuth";
import Config from "../config";

const baseURL = `${Config.URL_SERVER}/api`;

const instance = axios.create({
    baseURL,
    headers: { Accept: 'application/json'},
    withCredentials: true,
});

const useAxiosPrivate = () => {
    const refresh = useRefreshToken();
    const { auth } = useAuth();

    useEffect(() => {
        const reqIntercept = instance.interceptors.request.use(
            config => {
                if (!config.headers['Authorization']) {
                    config.headers['Authorization'] = `Bearer ${auth?.token}`;
                }
                return config;
            }, (error) => Promise.reject(error)
        )

        const resIntercept = instance.interceptors.response.use(
            (response) => response, async (error) => {
                const prevReq = error?.config;
                if (error?.response?.status === 401 && !prevReq?.sent) {
                    prevReq.sent = true;
                    const newAccessToken = await refresh();
                    if (!newAccessToken) return Promise.reject(error);
                    prevReq.headers['Authorization'] = `Bearer ${newAccessToken}`;
                    return instance(prevReq);
                }
                // A license lapsing mid-session makes every call fail. Reload so the
                // startup check renders the license screen instead of a stray error on
                // whichever page happened to be open. Keyed on `code` because ordinary
                // permission denials are 403 as well. The returned promise is left
                // unsettled on purpose, so no error surfaces while the page reloads.
                if (error?.response?.status === 403 && error?.response?.data?.code?.startsWith('LICENSE_')) {
                    window.location.reload();
                    return new Promise(() => {});
                }
                return Promise.reject(error);
            }
        );

        return () => {
            instance.interceptors.request.eject(reqIntercept);
            instance.interceptors.response.eject(resIntercept);
        }
    }, [auth]);

    return instance;
}

export default useAxiosPrivate;